import { Box, CircularProgress, Divider, Grid, InputAdornment, Tab, Tabs } from '@material-ui/core';
import { Button, Card, Icons, TextField, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { useInspectionList } from '../../rxdb/inspectionList/useInspectionList';
import useSelectedSiteDisciplineInspections from '../../rxdb/inspectionList/useSelectedSiteDisciplineInspections';
import useSelectedSiteDisciplineInspectors from '../../rxdb/viewInspector/useSelectedSiteDisciplineInspectors';
import { WorkPackages as WorkPackagesList } from '../../rxdb/workPackages/queryBuilder';
import { useWorkPackages } from '../../rxdb/workPackages/useWorkPackages';
import { useOnlineStatus } from '../../utilities';
import { IMAGE_PATH, WORK_PACKAGE_NAV_TAB } from '../../utilities/constants';
import { Discipline, InspectionStatus, TaskStatus, WorkPackageStatus } from '../../utilities/enums';
import {
  createObjectForBulkReAssignAndSign,
  getDisciplineById,
  getValueFromLocalStorage,
} from '../../utilities/utilityFunctions';
import InspectionCountStripe from '../InspectionCountStripe';
import InspectionComponent from '../InspectionList/InspectionList';
import InspectionListFilter from '../InspectionListFilter';
import { TabPanel } from '../InspectionListTabs/InspectionListTabs';
import InspectorInfo from '../InspectorInfo';
import OfflineDialog from '../OfflineDialog';
import ViewInspectors from '../ViewInspectors';
import useStyles from './WorkPackages.styles';

dayjs.extend(utc);

const WorkPackages: React.FC = () => {
  // hooks
  const classes = useStyles();
  const history = useHistory();
  const online = useOnlineStatus();
  const { SiteDiscipline, HandleGesture, configCatFlag, WorkPackageTabs, CurrentUserSelected } =
    useMst();
  const { workPackagesInitialized, getWorkPackages, workPackagesCollection } = useWorkPackages();
  const { inspectorData } = useSelectedSiteDisciplineInspectors(
    SiteDiscipline.siteId,
    SiteDiscipline.disciplineId,
  );
  const { liSignPendingInspections } = useSelectedSiteDisciplineInspections(
    SiteDiscipline.siteId,
    SiteDiscipline.disciplineId,
  );
  const { getInspectionTaskList } = useInspectionList();

  // states
  const [workPackageData, setWorkPackageData] = useState<WorkPackagesList[]>([]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [inProgress, setInProgress] = useState<boolean>(true);
  const [assigned, setAssigned] = useState<boolean>(true);
  const [inProgressStyle, setInProgressStyle] = useState<boolean>(false);
  const [assignedStyle, setAssignedStyle] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [openViewInspectors, setOpenViewInspectors] = useState<boolean>(false);
  const [openFIViewInspectors, setOpenFIViewInspectors] = useState<boolean>(false);
  const [openAddFieldInspector, setOpenAddFieldInspector] = useState<boolean>(false);
  const [isViewInspector, setIsViewInspector] = useState<boolean>(false);
  const [workPackageIdForInspector, setWorkPackageIdForInspector] = useState<number>(-1);
  const [isOfflineDialogOpen, setIsOfflineDialogOpen] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(WorkPackageTabs.tab);
  const [adhocCount, setAdhocCount] = useState<number>(0);
  const [ilTabValue, setIlTabValue] = useState<number>(0);
  const [ilSearchText, setIlSearchText] = useState<string>('');
  const [filterOneStyle, setFilterOneStyle] = useState<boolean>(false);
  const [filterTwoStyle, setFilterTwoStyle] = useState<boolean>(false);
  const [checkInspectionList, setCheckInspectionList] = useState<boolean>(false);
  const InspectionListREf = useRef<{
    handleSearchfromWP: (event: React.FocusEvent<HTMLInputElement>) => void;
    handleFilterOnefromWP: () => void;
    handleFilterTwofromWP: () => void;
    openWorkPackageFilesfromWP: () => void;
    endAndornmentSearchfromWP: () => void;
  }>();

  // constants
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const srcEmptyBox = `${IMAGE_PATH}/emptyBox.svg`;

  // functions
  const getWpDataFromRxdb = async () => {
    const workPackagesDocument = await getWorkPackages();
    if (workPackagesDocument) {
      const defaultVal = JSON.parse(getValueFromLocalStorage('selectedDiscipline'));
      const newCount = workPackagesDocument.length;
      let oldCount = defaultVal?.selectedDiscipline?.substring(
        defaultVal?.selectedDiscipline?.indexOf('(') + 1,
      );
      oldCount = oldCount.substring(0, oldCount.length - 1);

      if (newCount !== Number(oldCount)) {
        const newValue = {
          selectedDiscipline: `${getDisciplineById(
            defaultVal?.selectedDisciplineId,
          )} (${newCount})`,
          selectedDisciplineId: defaultVal.selectedDisciplineId,
        };
        localStorage.setItem('selectedDiscipline', JSON.stringify(newValue));
        SiteDiscipline.setDiscipline(newValue);
      }
      setAdhocCount(workPackagesDocument.filter(item => item.isAdhoc).length);
      setWorkPackageData(workPackagesDocument.filter(item => !item.isAdhoc));
    }
  };

  const handleInProgress = async () => {
    if (assigned) {
      setAssigned(false);
      setInProgressStyle(true);

      setAssignedStyle(false);
      setInProgress(true);
    }
    if (inProgress && !assigned) {
      setAssigned(true);
      setInProgress(true);

      setInProgressStyle(false);
      setAssignedStyle(false);
    }
  };

  const handleAssigned = async () => {
    if (inProgress) {
      setInProgress(false);
      setAssignedStyle(true);

      setInProgressStyle(false);
      setAssigned(true);
    }
    if (!inProgress && assigned) {
      setAssigned(true);
      setInProgress(true);

      setInProgressStyle(false);
      setAssignedStyle(false);
    }
  };

  const handleSearch = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const handleClick = (workPackageId: number) => {
    localStorage.setItem('workPackageId', String(workPackageId));
    CurrentUserSelected.setWorkPackageId(workPackageId);
    history.push('/inspection-list');
  };

  const getCount = () => {
    const searchedValues: string[] = [];
    const filteredValues: string[] = [];

    if (workPackageData.length > 0) {
      if (searchText.length > 0) {
        workPackageData.forEach((item: WorkPackagesList) => {
          if (
            item.workPackageName?.toLowerCase().includes(searchText?.toLowerCase()) ||
            String(item.workPackageId).includes(searchText)
          ) {
            if (searchedValues.indexOf(item.workPackageId) === -1) {
              searchedValues.push(item.workPackageId);
            }
          }
        });
      }

      if (assigned) {
        workPackageData.forEach((item: WorkPackagesList) => {
          if (item.workPackageStatusId === WorkPackageStatus.ASSIGNED) {
            filteredValues.push(item.workPackageId);
          }
        });
      }

      if (inProgress) {
        if (assigned) {
          workPackageData?.forEach((item: WorkPackagesList) => {
            if (filteredValues.indexOf(item.workPackageId) === -1) {
              filteredValues.push(item.workPackageId);
            }
          });
        } else {
          workPackageData.forEach((item: WorkPackagesList) => {
            if (item.workPackageStatusId === WorkPackageStatus.IN_PROGRESS) {
              if (filteredValues.indexOf(item.workPackageId) === -1) {
                filteredValues.push(item.workPackageId);
              }
            }
          });
        }
      } else if (assigned) {
        workPackageData.forEach((item: WorkPackagesList) => {
          if (item.workPackageStatusId === WorkPackageStatus.ASSIGNED) {
            if (filteredValues.indexOf(item.workPackageId) === -1) {
              filteredValues.push(item.workPackageId);
            }
          }
        });
      }

      if (searchText.length === 0) {
        return filteredValues.length;
      }
      if (searchText.length > 0 && searchedValues.length === 0) {
        return 0;
      }

      let count = 0;
      searchedValues.forEach(searchedItem => {
        filteredValues.forEach(filteredItem => {
          if (searchedItem === filteredItem) count++;
        });
      });
      return count | 0;
    }
    return 0;
  };

  const isSignAndStampButtonVisible = (wpId: number) => {
    const isPresent = liSignPendingInspections.find(
      inspection => inspection.workPackageId === wpId,
    );
    if (isPresent) return true;
    return false;
  };

  const handleClickOnSignAndStamp = async (wpId: number) => {
    const inspectionListData = await getInspectionTaskList(wpId);
    inspectionListData
      .filter(inspection => inspection.assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING)
      .forEach(inspection => {
        inspection?.taskList
          ?.filter(task => task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING)
          ?.forEach(task => {
            HandleGesture.insertIntoAccordion(String(task.fkAssetInspection));
            HandleGesture.insertIntoInspectionId(Number(inspection.inspectionId));
            HandleGesture.insertIntoTaskId(task.pkAssetInspectionTask);
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, inspection.assetInspectionStatusId),
              true,
            );
          });
      });
    HandleGesture.setGestureForSignAndStamp(true);
    HandleGesture.setGestureForReAssign(true);
    HandleGesture.setIsDefaultLiSignAndStamp(true);
    HandleGesture.setcomingFromWorkPackage(true);
    handleClick(wpId);
  };

  const endAndornmentSearch = () => {
    if (searchText.length !== 0) {
      return (
        <InputAdornment position="end">
          <Icons.XFeather
            color="primary"
            className={classes.searchTextField}
            onClick={() => setSearchText('')}
          />
        </InputAdornment>
      );
    }
  };

  const changeTab = (event: React.ChangeEvent<unknown>, newValue: number) => {
    setTabValue(newValue);
    WorkPackageTabs.setTab(newValue);
    localStorage.setItem('isAdhoc', JSON.stringify(Boolean(newValue)));
    localStorage.setItem('workPackageId', '');
    CurrentUserSelected.setWorkPackageId(0);
    CurrentUserSelected.setIsAdhoc(Boolean(newValue) || false);
  };

  const handleSearchAdhoc = (event: React.FocusEvent<HTMLInputElement>) => {
    if (InspectionListREf.current) {
      InspectionListREf.current.handleSearchfromWP(event);
    }
  };

  const handleFilterOne = () => {
    if (InspectionListREf.current) {
      InspectionListREf.current.handleFilterOnefromWP();
    }
  };

  const handleFilterTwo = () => {
    if (InspectionListREf.current) {
      InspectionListREf.current.handleFilterTwofromWP();
    }
  };

  const openWorkPackageFiles = () => {
    if (InspectionListREf.current) {
      InspectionListREf.current.openWorkPackageFilesfromWP();
    }
  };

  const endAndornmentSearchIL = () => {
    if (InspectionListREf.current) {
      InspectionListREf.current.endAndornmentSearchfromWP();
    }
  };

  // useEffects
  useEffect(() => {
    setSearchText('');
    setSearchBarOpen(false);
  }, [
    window.matchMedia('(orientation: portrait)').matches,
    window.matchMedia('(orientation: landscape)').matches,
  ]);

  useEffect(() => {
    let subscription: Subscription;
    if (workPackagesCollection && workPackagesInitialized) {
      subscription = workPackagesCollection.find().$.subscribe(getWpDataFromRxdb);
    }
    return () => {
      subscription?.unsubscribe();
    };
  }, [workPackagesInitialized, workPackagesCollection]);

  useEffect(() => {
    if (SiteDiscipline.refreshWorkPackage) {
      setWorkPackageData([]);
      SiteDiscipline.setRefreshWorkPackage(false);
    }
  }, [SiteDiscipline.refreshWorkPackage]);

  useEffect(() => {
    if (WorkPackageTabs.tab || WorkPackageTabs.tab === 0) {
      localStorage.removeItem('inspectionTabValue');
      if (WorkPackageTabs.tab === 0) localStorage.removeItem('adhocInspectionTabValue');
      setTabValue(Number(WorkPackageTabs.tab));
      changeTab(undefined, Number(WorkPackageTabs.tab));
    }
  }, [WorkPackageTabs.tab]);

  return (
    <>
      <Grid className={classes.workpackageContainer}>
        <Card elevation={1}>
          <Grid container spacing={2}>
            <Grid item container xs={12} alignItems="center">
              {searchBarOpen && window.matchMedia('(orientation: portrait)').matches ? (
                <Grid xs={12} item className={classes.searchBarOuterGrid}>
                  <TextField
                    fullWidth
                    noMinHeight
                    autoFocus
                    focused
                    variant="outlined"
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <Icons.SearchFeather color="primary" />
                      </InputAdornment>
                    }
                    className={classes.txtBgColor}
                    placeholder="Search"
                    onChange={handleSearch}
                  />
                  <Grid item className={classes.xFeatherGrid}>
                    <Icons.XFeather
                      color="primary"
                      onClick={() => {
                        setSearchText('');
                        setSearchBarOpen(false);
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Grid item xs={window.matchMedia('(orientation: portrait)').matches ? 5 : 6}>
                    <Box>
                      <Tabs
                        value={tabValue}
                        onChange={changeTab}
                        textColor="primary"
                        indicatorColor="primary"
                      >
                        <Tab
                          label={
                            isElectrical
                              ? `Routes (${getCount() | 0})`
                              : `Work packages (${getCount() | 0})`
                          }
                          className={classes.pageTitle}
                        />
                        <Tab
                          label={`Adhoc Inspections (${adhocCount | 0})`}
                          className={classes.pageTitle}
                        />
                      </Tabs>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={window.matchMedia('(orientation: portrait)').matches ? 7 : 6}
                    container
                    justifyContent="flex-end"
                  >
                    {tabValue === 0 && (
                      <>
                        <Grid item className={classes.quickFilterGrid}>
                          <Typography variant="body1" className={classes.quickFilterText}>
                            Quick Filter
                          </Typography>
                        </Grid>

                        <Grid item className={classes.inProgressButGrid}>
                          <Button
                            data-testid="inProgress"
                            id="inProgress"
                            color="secondary"
                            variant={inProgressStyle ? 'contained' : undefined}
                            noMinHeight
                            onClick={handleInProgress}
                            data-cy="in progress"
                            className={inProgressStyle ? 'inProgressBut' : 'notInProgressBut'}
                          >
                            <Typography variant="body1">In Progress</Typography>
                          </Button>
                        </Grid>

                        <Grid item className={classes.assignedButGrid}>
                          <Button
                            data-testid="assigned"
                            id="assigned"
                            color="secondary"
                            variant={assignedStyle ? 'contained' : undefined}
                            noMinHeight
                            onClick={handleAssigned}
                            data-cy="assigned"
                            className={assignedStyle ? 'assignedBut' : 'notAssignedBut'}
                          >
                            <Typography variant="body1">Assigned</Typography>
                          </Button>
                        </Grid>

                        <Grid item className={classes.dividerGrid}>
                          <Divider orientation="vertical" className={classes.dividerBg} />
                        </Grid>

                        {window.matchMedia('(orientation: portrait)').matches ? (
                          <Grid item className={classes.searchBarOuterGrid}>
                            <Icons.SearchFeather
                              color="primary"
                              onClick={() => setSearchBarOpen(true)}
                            />
                          </Grid>
                        ) : (
                          <Grid item className={classes.searchContainer}>
                            <TextField
                              data-testid="search-workpackes"
                              noMinHeight
                              variant="outlined"
                              focused
                              size="small"
                              type="text"
                              startAdornment={
                                <InputAdornment position="start">
                                  <Icons.SearchFeather color="primary" />
                                </InputAdornment>
                              }
                              endAdornment={endAndornmentSearch()}
                              value={searchText}
                              placeholder="Search"
                              className={classes.searchTextDiv}
                              onChange={handleSearch}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {tabValue === 1 && (
                      <InspectionListFilter
                        endAndornmentSearch={endAndornmentSearchIL}
                        filterOneStyle={filterOneStyle}
                        handleFilterTwo={handleFilterTwo}
                        handleFilterOne={handleFilterOne}
                        filterTwoStyle={filterTwoStyle}
                        tabValue={ilTabValue}
                        handleSearch={handleSearchAdhoc}
                        openWorkPackageFiles={openWorkPackageFiles}
                        searchText={ilSearchText}
                        checkInspectionList={checkInspectionList}
                      />
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <TabPanel value={tabValue} index={WORK_PACKAGE_NAV_TAB.WORKPACKAGE.VALUE}>
        <div
          className={
            configCatFlag.getBlueBannerDisplay === true ||
            configCatFlag.getRedBannerDisplay === true
              ? classes.root1
              : classes.root
          }
        >
          {workPackageData.length === 0 ? (
            <>
              {adhocCount !== 0 && (
                <Card className={classes.messageGrid}>
                  <img src={srcEmptyBox} className={classes.emptyBox} alt="" />
                  <Typography variant="h6" color="textSecondary" className={classes.divText}>
                    No active work packages for this site and discipline are available.
                  </Typography>
                </Card>
              )}
              {adhocCount === 0 && (
                <Grid item xs={12}>
                  {(adhocCount === 0 && SiteDiscipline.wpCount === 0) ||
                  SiteDiscipline.wpCount === null ? (
                    <Card className={classes.messageGrid}>
                      <img src={srcEmptyBox} className={classes.emptyBox} alt="" />
                      <Typography variant="h6" color="textSecondary" className={classes.divText}>
                        No active work packages for this site and discipline are available.
                      </Typography>
                    </Card>
                  ) : (
                    <Grid item xs={12} className={classes.emptyWorkpackagesContainer}>
                      <CircularProgress color="primary" />
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          ) : (
            <Card elevation={1} className={classes.workpackageCard}>
              {workPackageData
                .sort((a, b) => (a.workPackageStatusId <= b.workPackageStatusId ? 1 : -1))
                .map((val: WorkPackagesList) => {
                  return (
                    (val.workPackageName?.toLowerCase().includes(searchText?.toLowerCase()) ||
                      String(val.workPackageId).includes(searchText)) && (
                      <Card
                        onClick={() => handleClick(Number(val.workPackageId))}
                        elevation={1}
                        className={classes.workpackagesListContainer}
                        key={val.workPackageId}
                        hidden={
                          (!inProgress &&
                            (val.workPackageStatusId === WorkPackageStatus.IN_PROGRESS ||
                              val.workPackageStatusId === WorkPackageStatus.COMPLETED)) ||
                          (!assigned &&
                            (val.workPackageStatusId === WorkPackageStatus.ASSIGNED ||
                              val.workPackageStatusId === WorkPackageStatus.COMPLETED))
                        }
                      >
                        <Grid item container xs={12} alignItems="center">
                          <Grid
                            item
                            xs={
                              val.isLeadInspector &&
                              isSignAndStampButtonVisible(Number(val.workPackageId))
                                ? 6
                                : 8
                            }
                          >
                            <Typography variant="h4">
                              {`Id: ${val.workPackageId} - ${val.workPackageName} (${val.noOfInspections})`}
                            </Typography>
                            <Grid className={classes.addViewInspectorLabel}>
                              {getValueFromLocalStorage('isLeadInspector') === 'False' ||
                              !val.isLeadInspector ? (
                                <Typography
                                  display="block"
                                  color="primary"
                                  className={classes.ViewInspector}
                                  onClick={e => {
                                    setWorkPackageIdForInspector(Number(val.workPackageId));
                                    setOpenViewInspectors(true);
                                    e.stopPropagation();
                                    if (
                                      inspectorData.filter(
                                        v => v.workPackageId === Number(val.workPackageId),
                                      ).length === 0
                                    ) {
                                      if (online) {
                                        setOpenAddFieldInspector(true);
                                        setIsViewInspector(false);
                                      } else {
                                        setIsOfflineDialogOpen(true);
                                        setOpenAddFieldInspector(false);
                                        setOpenFIViewInspectors(false);
                                      }
                                    } else {
                                      setOpenAddFieldInspector(false);
                                      setIsViewInspector(true);
                                    }
                                  }}
                                >
                                  View Inspectors
                                </Typography>
                              ) : (
                                <Typography
                                  data-testid="dialog-title-fi"
                                  display="block"
                                  color="primary"
                                  className={classes.ViewInspector}
                                  onClick={e => {
                                    setWorkPackageIdForInspector(Number(val.workPackageId));
                                    setOpenFIViewInspectors(true);
                                    e.stopPropagation();
                                    if (
                                      inspectorData.filter(
                                        v => v.workPackageId === Number(val.workPackageId),
                                      ).length === 0
                                    ) {
                                      setOpenAddFieldInspector(true);
                                      setIsViewInspector(false);
                                    } else {
                                      setOpenAddFieldInspector(false);
                                      setIsViewInspector(true);
                                    }
                                  }}
                                >
                                  {inspectorData.filter(
                                    v => v.workPackageId === Number(val.workPackageId),
                                  ).length > 0
                                    ? 'View Inspectors'
                                    : 'Add Field Inspector'}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>

                          {val.isLeadInspector &&
                            isSignAndStampButtonVisible(Number(val.workPackageId)) && (
                              <Grid
                                item
                                xs={2}
                                container
                                justifyContent="flex-end"
                                alignItems="flex-end"
                                className={classes.signStampButtonGrid}
                              >
                                <Button
                                  color="primary"
                                  className={classes.signStampButton}
                                  noMinHeight
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (online)
                                      handleClickOnSignAndStamp(Number(val.workPackageId));
                                    else setIsOfflineDialogOpen(true);
                                  }}
                                >
                                  <Typography variant="subtitle2">Sign & Stamp</Typography>
                                </Button>
                              </Grid>
                            )}

                          <Grid item xs={4}>
                            <Grid
                              container
                              justifyContent="flex-end"
                              alignItems="center"
                              className={classes.statusGrid}
                              xs={12}
                            >
                              <Grid
                                item
                                container
                                xs={9}
                                alignItems="flex-end"
                                alignContent="flex-end"
                                className={classes.individualStatusGrid}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                  color={
                                    val.workPackageStatusId === WorkPackageStatus.IN_PROGRESS
                                      ? 'primary'
                                      : 'secondary'
                                  }
                                  className={
                                    val.workPackageStatusId === WorkPackageStatus.COMPLETED
                                      ? 'statusCompletedText'
                                      : 'statusNotCompletedText'
                                  }
                                >
                                  {val.workPackageStatusId === WorkPackageStatus.IN_PROGRESS
                                    ? 'In Progress'
                                    : [
                                        WorkPackageStatus.PLANNED,
                                        WorkPackageStatus.ASSIGNED,
                                      ].includes(val.workPackageStatusId)
                                    ? 'Assigned'
                                    : 'Completed'}
                                </Typography>
                                <InspectionCountStripe workpackageId={Number(val.workPackageId)} />
                              </Grid>
                              <Icons.ChevronRightFeather color="primary" />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    )
                  );
                })}
              <div />
            </Card>
          )}
        </div>
      </TabPanel>
      <TabPanel value={tabValue} index={WORK_PACKAGE_NAV_TAB.ADHOC.VALUE}>
        <InspectionComponent
          ref={InspectionListREf}
          setTabValue={setIlTabValue}
          setFilterOneStyleAdhoc={setFilterOneStyle}
          setFilterTwoStyleAdhoc={setFilterTwoStyle}
          setILSearchText={setIlSearchText}
          setCheckInspectionList={setCheckInspectionList}
        />
      </TabPanel>
      {openViewInspectors && (
        <InspectorInfo
          open={openViewInspectors}
          onClose={() => setOpenViewInspectors(false)}
          workPackageId={workPackageIdForInspector}
        />
      )}

      {openFIViewInspectors && (
        <ViewInspectors
          openViewInspectors={openFIViewInspectors}
          setOpenViewInspectors={setOpenFIViewInspectors}
          openAddFieldInspector={openAddFieldInspector}
          setOpenAddFieldInspector={setOpenAddFieldInspector}
          workPackageId={workPackageIdForInspector}
          isViewInspectors={isViewInspector}
        />
      )}

      {isOfflineDialogOpen && (
        <OfflineDialog open={isOfflineDialogOpen} onClose={() => setIsOfflineDialogOpen(false)} />
      )}
    </>
  );
};

export default observer(WorkPackages);
